import { Modal, Box, IconButton, Typography, Button, useMediaQuery, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAccount, resetUserActions } from '~/actions';
import { RootState, UserState } from '~/types';
import { STATUS } from '~/literals';
import { useEffect } from 'react';

interface DeleteAccountModalProps {
  onClose: () => void;
  open: boolean;
}

const DeleteAccountModal = ({ open, onClose }: DeleteAccountModalProps) => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();
  const status = useSelector<RootState, string>(({ user }) => user?.status || STATUS.IDLE);
  const user = useSelector<RootState, UserState | null>(({ user }) => user);

  const handleDelete = () => {
    dispatch(deleteAccount());
  };

  useEffect(() => {
    dispatch(resetUserActions());
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 5,
          maxHeight: '90%',
          overflowY: 'auto',
          minWidth: isMdDown ? '80%' : 'auto',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 10, right: 10 }}
        >
          <CloseIcon />
        </IconButton>

        {status === STATUS.RUNNING ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : user?.userDeleteSuccess ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your account has been successfully deleted.
          </Alert>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Are you sure you want to delete your account?
            </Typography>

            {user?.userDeleteErrorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to delete account. Please try again.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="contained"
                onClick={onClose}
              >
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                {user?.userDeleteErrorMessage ? 'Retry' : 'Yes'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default DeleteAccountModal;